import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import GstRegistrationSection from './gst/GstRegistrationSection';
import { gstStatusMeta } from '../../lib/gstStatusCatalog';
import {
  Loader2, ArrowLeft, Building2, ClipboardCheck, FileText, IndianRupee,
  LayoutGrid, AlertTriangle, ChevronRight, Search, Mail, X, Send,
} from 'lucide-react';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const humanize = (s) => (s ? String(s).replace(/_/g, ' ') : '—');

const STATUS_CLR = (s) => {
  if (['filed', 'filed_complete', 'complete', 'nil_filed', 'documents_approved', 'approved_by_client', 'tax_paid'].includes(s)) return 'bg-emerald-500/20 text-emerald-300';
  if (['tax_pending', 'client_confirmation_pending', 'sent_for_client_approval', 'changes_requested'].includes(s)) return 'bg-amber-500/20 text-amber-300';
  if (['filed_acknowledgement_pending', 'acknowledgement_pending', 'under_review'].includes(s)) return 'bg-blue-500/20 text-blue-300';
  return 'bg-slate-700/40 text-slate-300';
};
const PAY_CLR = (s) => {
  if (['paid', 'paid_offline_approved', 'covered_until_date', 'complimentary', 'waived'].includes(s)) return 'bg-emerald-500/20 text-emerald-300';
  if (['overdue', 'unpaid', 'expired'].includes(s)) return 'bg-red-500/20 text-red-300';
  return 'bg-amber-500/20 text-amber-300';
};

const MetricCard = ({ label, value, tone = 'default', testId }) => {
  const tones = {
    default: 'border-emerald-900/40 text-white',
    warn: 'border-amber-500/40 text-amber-300',
    danger: 'border-red-500/40 text-red-300',
    ok: 'border-emerald-500/40 text-emerald-300',
  };
  return (
    <div className={`rounded-xl border ${tones[tone]} bg-[#0f1611] p-4`} data-testid={testId}>
      <div className="text-2xl font-semibold">{value ?? '—'}</div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 mt-1">{label}</div>
    </div>
  );
};

const TABS = [
  { id: 'summary', label: 'Portfolio Summary', icon: LayoutGrid },
  { id: 'companies', label: 'GST Company List', icon: Building2 },
  { id: 'filings', label: 'Filing Tracker', icon: FileText },
  { id: 'payments', label: 'Payment Tracker', icon: IndianRupee },
];

const PortfolioSummary = () => {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/admin/gst-compliance/summary').then((r) => setS(r.data))
      .catch(() => toast.error('Failed to load summary')).finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-emerald-500" /></div>;
  if (!s) return null;
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="gst-portfolio-summary">
      <MetricCard label="Total companies" value={s.total_companies} testId="metric-total-companies" />
      <MetricCard label="GST-enabled companies" value={s.gst_enabled_companies} tone="ok" />
      <MetricCard label="Active GSTINs" value={s.active_gstins} tone="ok" testId="metric-active-gstins" />
      <MetricCard label="Monthly registrations" value={s.monthly_registrations} />
      <MetricCard label="QRMP registrations" value={s.qrmp_registrations} />
      <MetricCard label="Composition registrations" value={s.composition_registrations} />
      <MetricCard label="Payment pending" value={s.payment_pending} tone="warn" />
      <MetricCard label="Documents pending" value={s.documents_pending} tone="warn" />
      <MetricCard label="Client confirmation pending" value={s.client_confirmation_pending} tone="warn" />
      <MetricCard label="Tax pending" value={s.tax_pending} tone="warn" />
      <MetricCard label="Acknowledgement pending" value={s.acknowledgement_pending} tone="warn" />
      <MetricCard label="Completed filings" value={s.completed_filings} tone="ok" />
      <MetricCard label="Overdue filings" value={s.overdue_filings} tone="danger" testId="metric-overdue" />
    </div>
  );
};

const FilterInput = ({ label, ...props }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
    <input {...props} className="w-full bg-[#080b09] border border-emerald-900/40 rounded px-2 py-1.5 text-sm text-white" />
  </div>
);
const FilterSelect = ({ label, children, ...props }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
    <select {...props} className="w-full bg-[#080b09] border border-emerald-900/40 rounded px-2 py-1.5 text-sm text-white">{children}</select>
  </div>
);

const CompanyList = ({ onOpen }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opts, setOpts] = useState({ gstins: [], states: [], payment_statuses: [] });
  const [f, setF] = useState({ gstin: '', state: '', scheme: '', payment_status: '', verification_status: '' });
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
      const r = await api.get('/admin/gst-compliance/registrations', { params });
      setRows(r.data.registrations || []);
    } catch { toast.error('Failed to load registrations'); } finally { setLoading(false); }
  }, [f]);
  useEffect(() => { load(); }, [load]);
  // Build filter options once from the full (unfiltered) registration set.
  useEffect(() => {
    api.get('/admin/gst-compliance/registrations').then((r) => {
      const all = r.data.registrations || [];
      const uniq = (arr) => [...new Set(arr.filter(Boolean))];
      setOpts({
        gstins: uniq(all.map((x) => x.gstin)).sort(),
        states: uniq(all.map((x) => x.state)).sort(),
        payment_statuses: uniq(all.map((x) => x.payment_status)).sort(),
      });
    }).catch(() => {});
  }, []);

  const grouped = useMemo(() => {
    const g = {};
    rows.forEach((r) => { (g[r.company_id] = g[r.company_id] || { name: r.company_name, id: r.company_id, regs: [] }).regs.push(r); });
    return Object.values(g);
  }, [rows]);

  return (
    <div className="space-y-4" data-testid="gst-company-list">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <FilterSelect label="GSTIN" value={f.gstin} onChange={(e) => setF({ ...f, gstin: e.target.value })} data-testid="filter-company-gstin">
          <option value="">All</option>{opts.gstins.map((g) => <option key={g} value={g}>{g}</option>)}
        </FilterSelect>
        <FilterSelect label="State" value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} data-testid="filter-company-state">
          <option value="">All</option>{opts.states.map((s) => <option key={s} value={s}>{s}</option>)}
        </FilterSelect>
        <FilterSelect label="Scheme" value={f.scheme} onChange={(e) => setF({ ...f, scheme: e.target.value })} data-testid="filter-scheme">
          <option value="">All</option><option>Monthly</option><option>QRMP</option><option>Composition</option>
        </FilterSelect>
        <FilterSelect label="Verification" value={f.verification_status} onChange={(e) => setF({ ...f, verification_status: e.target.value })}>
          <option value="">All</option><option value="pending">Pending</option><option value="verified">Verified</option><option value="disputed">Disputed</option>
        </FilterSelect>
        <FilterSelect label="Payment status" value={f.payment_status} onChange={(e) => setF({ ...f, payment_status: e.target.value })} data-testid="filter-payment-status">
          <option value="">All</option>{opts.payment_statuses.map((p) => <option key={p} value={p}>{humanize(p)}</option>)}
        </FilterSelect>
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>
        : grouped.length === 0 ? <p className="text-sm text-slate-500 py-8 text-center">No GST registrations match.</p>
        : grouped.map((g) => (
          <div key={g.id} className="rounded-xl border border-emerald-900/30 bg-[#0f1611] overflow-hidden" data-testid={`gst-company-group-${g.id}`}>
            <div className="px-4 py-2.5 bg-[#080b09] flex items-center justify-between">
              <div className="text-white font-medium text-sm flex items-center gap-2"><Building2 className="w-4 h-4 text-emerald-400" /> {g.name} <span className="text-[11px] text-slate-500">· {g.id}</span></div>
              <span className="text-[11px] text-slate-500">{g.regs.length} GSTIN{g.regs.length > 1 ? 's' : ''}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[980px] text-sm">
                <thead><tr className="text-[10px] uppercase tracking-wider text-slate-500">
                  {['GSTIN', 'State', 'Scheme', 'Verification', 'Current filing', 'Payment', 'Service period', 'Docs', 'Client conf.', 'Next due', 'Staff', ''].map((h, i) => <th key={i} className="px-3 py-2 text-left font-medium">{h}</th>)}
                </tr></thead>
                <tbody>
                  {g.regs.map((r) => (
                    <tr key={r.registration_id} className="border-t border-emerald-900/20" data-testid={`gst-reg-row-${r.registration_id}`}>
                      <td className="px-3 py-2 font-mono text-emerald-300 text-[12px]">{r.gstin}</td>
                      <td className="px-3 py-2 text-slate-300">{r.state}</td>
                      <td className="px-3 py-2 text-slate-300">{r.filing_scheme}</td>
                      <td className="px-3 py-2"><span className={`text-[11px] px-2 py-0.5 rounded ${r.verification_status === 'verified' ? 'bg-emerald-500/20 text-emerald-300' : r.verification_status === 'disputed' ? 'bg-red-500/20 text-red-300' : 'bg-amber-500/20 text-amber-300'}`}>{r.verification_status}</span></td>
                      <td className="px-3 py-2 text-slate-300">
                        {r.current_filing || '—'}
                        {r.current_filing && <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded ${gstStatusMeta(r.current_filing_status).cls}`} title={gstStatusMeta(r.current_filing_status).description}>{gstStatusMeta(r.current_filing_status).label}</span>}
                      </td>
                      <td className="px-3 py-2"><span className={`text-[11px] px-2 py-0.5 rounded ${PAY_CLR(r.payment_status)}`}>{humanize(r.payment_status)}</span></td>
                      <td className="px-3 py-2 text-slate-400 text-[11px]">{r.service_period ? r.service_period.replace(/-/g, '/') : '—'}</td>
                      <td className="px-3 py-2 text-slate-300 text-[11px]">{humanize(r.documents_status)}</td>
                      <td className="px-3 py-2 text-slate-300 text-[11px]">{humanize(r.client_confirmation_status)}</td>
                      <td className={`px-3 py-2 text-[11px] ${r.is_overdue ? 'text-red-300 font-medium' : 'text-slate-400'}`}>{fmtDate(r.next_due_date)}{r.is_overdue ? ' ⚠' : ''}</td>
                      <td className="px-3 py-2 text-slate-400 text-[11px]">{r.assigned_staff || '—'}</td>
                      <td className="px-3 py-2">
                        <button onClick={() => onOpen(r.company_id)} data-testid={`gst-open-${r.registration_id}`}
                          className="text-[12px] text-emerald-300 hover:text-white border border-emerald-500/40 rounded px-2 py-1 flex items-center gap-1">Open <ChevronRight className="w-3 h-3" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
    </div>
  );
};

const RETURN_TYPES = ['GSTR-1', 'GSTR-3B', 'PMT-06', 'IFF', 'GSTR-9', 'GSTR-9C', 'CMP-08', 'LUT'];

const PLACEHOLDER_HINTS = ['{client_name}', '{company_name}', '{gstin}', '{period}', '{required_list}', '{portal_link}', '{team_name}'];

const EmailReminderModal = ({ count, onClose, onSent, filingIds }) => {
  const [templates, setTemplates] = useState([]);
  const [tplKey, setTplKey] = useState('');
  const [variantKey, setVariantKey] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get('/admin/gst-compliance/reminder-templates').then((r) => {
      const tpls = r.data.templates || [];
      setTemplates(tpls);
      if (tpls.length) {
        setTplKey(tpls[0].key);
        const v = tpls[0].variants[0];
        setVariantKey(v.key); setSubject(v.subject); setBody(v.body);
      }
    }).catch(() => toast.error('Failed to load email templates'));
  }, []);

  const applyVariant = (tKey, vKey) => {
    const tpl = templates.find((t) => t.key === tKey);
    const v = tpl?.variants.find((x) => x.key === vKey) || tpl?.variants[0];
    if (v) { setSubject(v.subject); setBody(v.body); setVariantKey(v.key); }
  };

  const send = async () => {
    if (!subject.trim() || !body.trim()) { toast.error('Subject and message are required'); return; }
    setSending(true);
    try {
      const r = await api.post('/admin/gst-compliance/send-reminders', {
        filing_ids: filingIds, template_key: tplKey, variant: variantKey, subject, body,
      });
      const skipped = r.data.skipped_no_email || 0;
      toast.success(`Queued ${r.data.queued} email(s)${skipped ? ` · ${skipped} skipped (no client email)` : ''}`);
      onSent();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to send emails'); }
    finally { setSending(false); }
  };

  const curTpl = templates.find((t) => t.key === tplKey);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="email-reminder-modal">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl border border-emerald-900/40 bg-[#0f1611] p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2"><Mail className="w-5 h-5 text-emerald-400" /> Send email reminder</h3>
          <button onClick={onClose} data-testid="email-modal-close" className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="text-[12px] text-slate-400 mb-4">
          Sending <span className="text-emerald-300 font-medium">1 email per filing</span> to the company owner's email — <span className="text-emerald-300 font-medium">{count}</span> filing(s) selected.
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Email type</div>
            <select value={tplKey} data-testid="email-template-select"
                    onChange={(e) => { setTplKey(e.target.value); const t = templates.find((x) => x.key === e.target.value); if (t) applyVariant(e.target.value, t.variants[0].key); }}
                    className="w-full bg-[#080b09] border border-emerald-900/40 rounded px-2 py-1.5 text-sm text-white">
              {templates.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Variant</div>
            <select value={variantKey} data-testid="email-variant-select"
                    onChange={(e) => applyVariant(tplKey, e.target.value)}
                    className="w-full bg-[#080b09] border border-emerald-900/40 rounded px-2 py-1.5 text-sm text-white">
              {curTpl?.variants.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
            </select>
          </div>
        </div>
        <div className="mb-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Subject</div>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="email-subject-input"
                 className="w-full bg-[#080b09] border border-emerald-900/40 rounded px-2 py-1.5 text-sm text-white" />
        </div>
        <div className="mb-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Message</div>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={12} data-testid="email-body-input"
                    className="w-full bg-[#080b09] border border-emerald-900/40 rounded px-2 py-1.5 text-sm text-white font-mono leading-relaxed" />
        </div>
        <div className="text-[11px] text-slate-500 mb-4">
          Auto-filled per filing: {PLACEHOLDER_HINTS.map((p) => <code key={p} className="text-emerald-400/80 mr-1">{p}</code>)}
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded text-sm text-slate-300 border border-slate-700 hover:bg-slate-800">Cancel</button>
          <button onClick={send} disabled={sending} data-testid="email-send-btn"
                  className="px-4 py-1.5 rounded text-sm font-medium bg-emerald-500 hover:bg-emerald-400 text-black disabled:opacity-50 flex items-center gap-1.5">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send {count} email(s)
          </button>
        </div>
      </div>
    </div>
  );
};

const FilingTracker = ({ onOpen }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opts, setOpts] = useState({ gstins: [], states: [], statuses: [], fys: [], months: [] });
  const [selected, setSelected] = useState(() => new Set());
  const [showEmail, setShowEmail] = useState(false);
  const [f, setF] = useState({ gstin: '', state: '', return_type: '', status: '', financial_year: '', month: '', quarter: '', due_from: '', due_to: '' });
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
      const r = await api.get('/admin/gst-compliance/filings', { params });
      setRows(r.data.filings || []);
    } catch { toast.error('Failed to load filings'); } finally { setLoading(false); }
  }, [f]);
  useEffect(() => { load(); }, [load]);
  // Build filter options once from the full (unfiltered) filing set.
  useEffect(() => {
    api.get('/admin/gst-compliance/filings', { params: { limit: 500 } }).then((r) => {
      const all = r.data.filings || [];
      const uniq = (arr) => [...new Set(arr.filter(Boolean))];
      const monthRe = /^\d{4}-\d{2}$/;
      setOpts({
        gstins: uniq(all.map((x) => x.gstin)).sort(),
        states: uniq(all.map((x) => x.state)).sort(),
        statuses: uniq(all.map((x) => x.status)).sort(),
        fys: uniq(all.map((x) => x.financial_year)).sort().reverse(),
        months: uniq(all.map((x) => x.filing_period).filter((m) => monthRe.test(m || ''))).sort().reverse(),
      });
    }).catch(() => {});
  }, []);

  const toggleOne = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const visibleIds = rows.map((r) => r.filing_id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const toggleAll = () => setSelected((prev) => {
    const next = new Set(prev);
    if (allVisibleSelected) visibleIds.forEach((id) => next.delete(id));
    else visibleIds.forEach((id) => next.add(id));
    return next;
  });
  const selectedIds = [...selected];

  return (
    <div className="space-y-4" data-testid="gst-filing-tracker">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
        <FilterSelect label="GSTIN" value={f.gstin} onChange={(e) => setF({ ...f, gstin: e.target.value })} data-testid="filter-gstin">
          <option value="">All</option>{opts.gstins.map((g) => <option key={g} value={g}>{g}</option>)}
        </FilterSelect>
        <FilterSelect label="State" value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} data-testid="filter-state">
          <option value="">All</option>{opts.states.map((s) => <option key={s} value={s}>{s}</option>)}
        </FilterSelect>
        <FilterSelect label="Return type" value={f.return_type} onChange={(e) => setF({ ...f, return_type: e.target.value })} data-testid="filter-return-type">
          <option value="">All</option>{RETURN_TYPES.map((t) => <option key={t}>{t}</option>)}
        </FilterSelect>
        <FilterSelect label="Status" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="filter-status">
          <option value="">All</option>{opts.statuses.map((s) => <option key={s} value={s}>{gstStatusMeta(s).label}</option>)}
        </FilterSelect>
        <FilterSelect label="Financial year" value={f.financial_year} onChange={(e) => setF({ ...f, financial_year: e.target.value })} data-testid="filter-fy">
          <option value="">All</option>{opts.fys.map((y) => <option key={y} value={y}>{y}</option>)}
        </FilterSelect>
        <FilterSelect label="Quarter" value={f.quarter} onChange={(e) => setF({ ...f, quarter: e.target.value })} data-testid="filter-quarter">
          <option value="">All</option><option>Q1</option><option>Q2</option><option>Q3</option><option>Q4</option>
        </FilterSelect>
        <FilterSelect label="Month" value={f.month} onChange={(e) => setF({ ...f, month: e.target.value })} data-testid="filter-month">
          <option value="">All</option>{opts.months.map((m) => <option key={m} value={m}>{m}</option>)}
        </FilterSelect>
        <FilterInput label="Due from" type="date" value={f.due_from} onChange={(e) => setF({ ...f, due_from: e.target.value })} data-testid="filter-due-from" />
        <FilterInput label="Due to" type="date" value={f.due_to} onChange={(e) => setF({ ...f, due_to: e.target.value })} data-testid="filter-due-to" />
      </div>
      <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="filing-tracker-actionbar">
        <div className="text-[12px] text-slate-400">
          {selected.size > 0
            ? <span><span className="text-emerald-300 font-medium">{selected.size}</span> selected</span>
            : <span>Tick companies to send a bulk email reminder</span>}
        </div>
        <div className="flex items-center gap-2">
          {selected.size > 0 && (
            <button onClick={() => setSelected(new Set())} data-testid="clear-selection-btn"
                    className="text-[12px] px-2.5 py-1.5 rounded border border-slate-700 text-slate-300 hover:bg-slate-800">Clear</button>
          )}
          <button onClick={() => setShowEmail(true)} disabled={selected.size === 0} data-testid="send-email-btn"
                  className="text-[12px] px-3 py-1.5 rounded font-medium bg-emerald-500 hover:bg-emerald-400 text-black disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5">
            <Mail className="w-4 h-4" /> Send email{selected.size > 0 ? ` (${selected.size})` : ''}
          </button>
        </div>
      </div>
      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div> : (
        <div className="overflow-x-auto rounded-xl border border-emerald-900/30">
          <table className="w-full min-w-[1000px] text-sm">
            <thead className="bg-[#080b09]"><tr className="text-[10px] uppercase tracking-wider text-slate-500">
              <th className="px-3 py-2 text-left font-medium">
                <input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} data-testid="select-all-checkbox"
                       className="accent-emerald-500 cursor-pointer" title="Select all" />
              </th>
              {['Company', 'GSTIN', 'State', 'Return', 'FY', 'Period', 'Status', 'Docs', 'ARN', 'Internal due', 'Client due', 'Staff', ''].map((h, i) => <th key={i} className="px-3 py-2 text-left font-medium">{h}</th>)}
            </tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={14} className="text-center text-slate-500 py-8">No filings match.</td></tr>
                : rows.map((r) => (
                  <tr key={r.filing_id} className={`border-t border-emerald-900/20 ${selected.has(r.filing_id) ? 'bg-emerald-500/5' : ''}`} data-testid={`gst-filing-tracker-row-${r.filing_id}`}>
                    <td className="px-3 py-2">
                      <input type="checkbox" checked={selected.has(r.filing_id)} onChange={() => toggleOne(r.filing_id)}
                             data-testid={`row-checkbox-${r.filing_id}`} className="accent-emerald-500 cursor-pointer" />
                    </td>
                    <td className="px-3 py-2 text-white text-[12px]">{r.company_name}</td>
                    <td className="px-3 py-2 font-mono text-emerald-300 text-[11px]">{r.gstin}</td>
                    <td className="px-3 py-2 text-slate-400 text-[11px]">{r.state}</td>
                    <td className="px-3 py-2 text-slate-300">{r.return_type}</td>
                    <td className="px-3 py-2 text-slate-400 text-[11px]">{r.financial_year}</td>
                    <td className="px-3 py-2 text-slate-300 text-[11px]">{r.filing_period}</td>
                    <td className="px-3 py-2"><span className={`text-[11px] px-2 py-0.5 rounded ${gstStatusMeta(r.status).cls}`} title={gstStatusMeta(r.status).description}>{gstStatusMeta(r.status).label}</span></td>
                    <td className="px-3 py-2 text-slate-400 text-[11px]">{r.documents_count} ({r.acknowledgement_count} ack)</td>
                    <td className="px-3 py-2 text-slate-400 text-[11px]">{r.arn || '—'}</td>
                    <td className={`px-3 py-2 text-[11px] ${r.is_overdue ? 'text-red-300' : 'text-slate-400'}`}>{fmtDate(r.internal_due_date)}{r.is_overdue ? ' ⚠' : ''}</td>
                    <td className="px-3 py-2 text-slate-400 text-[11px]">{fmtDate(r.client_due_date)}</td>
                    <td className="px-3 py-2 text-slate-400 text-[11px]">{r.assigned_staff || '—'}</td>
                    <td className="px-3 py-2"><button onClick={() => onOpen(r.company_id)} className="text-[12px] text-emerald-300 hover:text-white">Open</button></td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
      {showEmail && (
        <EmailReminderModal
          count={selected.size}
          filingIds={selectedIds}
          onClose={() => setShowEmail(false)}
          onSent={() => { setShowEmail(false); setSelected(new Set()); }}
        />
      )}
    </div>
  );
};

const PSTATE_CLR = {
  paid: 'bg-emerald-500/20 text-emerald-300', paid_offline_approved: 'bg-emerald-500/20 text-emerald-300',
  due: 'bg-blue-500/20 text-blue-300', overdue: 'bg-amber-500/20 text-amber-300',
  payment_hold: 'bg-red-500/20 text-red-300', offline_pending_approval: 'bg-purple-500/20 text-purple-300',
  complimentary: 'bg-indigo-500/20 text-indigo-300',
};
const rupee = (n) => (n == null ? '—' : `₹${Number(n).toLocaleString('en-IN')}`);
const BUCKETS = [
  ['all', 'All'], ['due', 'Due'], ['overdue', 'Overdue'], ['grace', 'Grace'],
  ['payment_hold', 'On hold'], ['offline_pending_approval', 'Offline pending'],
  ['paid', 'Paid'], ['complimentary', 'Complimentary'], ['refunded', 'Refunded'],
];

const PaymentTracker = ({ onOpen }) => {
  const [data, setData] = useState({ rows: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = React.useCallback(() => {
    setLoading(true);
    api.get('/admin/gst-compliance/billing-periods', { params: filter === 'all' ? {} : { status_filter: filter } })
      .then((r) => setData(r.data)).catch(() => toast.error('Failed to load billing periods'))
      .finally(() => setLoading(false));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="gst-payment-tracker">
      {/* Summary buckets / filters */}
      <div className="flex flex-wrap gap-2">
        {BUCKETS.map(([id, label]) => {
          const s = data.summary || {};
          const count = id === 'all'
            ? ['due', 'overdue', 'payment_hold', 'offline_pending_approval', 'paid', 'complimentary', 'refunded'].reduce((a, k) => a + (s[k] || 0), 0)
            : (s[id] ?? 0);
          const active = filter === id;
          return (
            <button key={id} onClick={() => setFilter(id)} data-testid={`gst-tracker-filter-${id}`}
              className={`px-3 py-1.5 rounded-lg text-[12px] border transition-colors ${active ? 'bg-emerald-500 text-black border-emerald-500' : 'border-emerald-900/40 text-slate-300 hover:text-white'}`}>
              {label} <span className="opacity-70">({count})</span>
            </button>
          );
        })}
      </div>
      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div> : (
        <div className="overflow-x-auto rounded-xl border border-emerald-900/30">
          <table className="w-full min-w-[1100px] text-sm">
            <thead className="bg-[#080b09]"><tr className="text-[10px] uppercase tracking-wider text-slate-500">
              {['Company', 'GSTIN', 'State', 'Freq', 'Billing period', 'Standard', 'Discount', 'Final', 'Due date', 'Status', 'Method', 'Invoice', 'Staff', ''].map((h, i) => <th key={i} className="px-3 py-2 text-left font-medium whitespace-nowrap">{h}</th>)}
            </tr></thead>
            <tbody>
              {(data.rows || []).length === 0 ? <tr><td colSpan={14} className="text-center text-slate-500 py-8">No billing periods.</td></tr>
                : data.rows.map((r) => (
                  <tr key={r.billing_period_id} className="border-t border-emerald-900/20" data-testid={`gst-payment-row-${r.billing_period_id}`}>
                    <td className="px-3 py-2 text-white text-[12px] whitespace-nowrap">{r.company_name}</td>
                    <td className="px-3 py-2 font-mono text-emerald-300 text-[11px]">{r.gstin}</td>
                    <td className="px-3 py-2 text-slate-400 text-[11px]">{r.state || '—'}</td>
                    <td className="px-3 py-2 text-slate-300 text-[11px] capitalize">{r.frequency}</td>
                    <td className="px-3 py-2 text-slate-300 text-[11px] whitespace-nowrap">{r.period_start} → {r.period_end}</td>
                    <td className="px-3 py-2 text-slate-400 text-[11px]">{rupee(r.standard_amount)}</td>
                    <td className="px-3 py-2 text-amber-300 text-[11px]">{r.discount_value ? `${r.discount_value}${r.discount_type === 'percentage' ? '%' : '₹'}` : '—'}</td>
                    <td className="px-3 py-2 text-emerald-300 text-[11px]">{rupee(r.final_amount)}</td>
                    <td className="px-3 py-2 text-slate-400 text-[11px] whitespace-nowrap">{r.payment_due_date || '—'}</td>
                    <td className="px-3 py-2"><span className={`text-[10px] px-2 py-0.5 rounded whitespace-nowrap ${PSTATE_CLR[r.payment_state] || 'bg-slate-700 text-slate-300'}`}>{(r.payment_state || '').replace(/_/g, ' ')}</span></td>
                    <td className="px-3 py-2 text-slate-400 text-[11px]">{r.payment_method || '—'}</td>
                    <td className="px-3 py-2 text-[11px]">{r.invoice_id ? <a href={`/api/invoices/${r.invoice_id}/pdf?token=${localStorage.getItem('token')}`} target="_blank" rel="noopener noreferrer" className="text-emerald-300 hover:text-white">View</a> : '—'}</td>
                    <td className="px-3 py-2 text-slate-400 text-[11px]">{r.assigned_staff || '—'}</td>
                    <td className="px-3 py-2"><button onClick={() => onOpen(r.company_id)} data-testid={`gst-payment-open-${r.billing_period_id}`} className="text-[12px] text-emerald-300 hover:text-white whitespace-nowrap">Open</button></td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export const GstCompliance = () => {
  const [tab, setTab] = useState('summary');
  const [openCompany, setOpenCompany] = useState(null);

  if (openCompany) {
    return (
      <div className="space-y-4">
        <button onClick={() => setOpenCompany(null)} data-testid="gst-compliance-back"
          className="text-sm text-slate-300 hover:text-white flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Back to GST Compliance</button>
        <GstRegistrationSection companyId={openCompany} />
      </div>
    );
  }
  return (
    <div className="space-y-5" data-testid="gst-compliance">
      <div>
        <h1 className="text-2xl font-semibold text-white flex items-center gap-2"><ClipboardCheck className="w-6 h-6 text-emerald-400" /> GST Compliance</h1>
        <p className="text-sm text-slate-500 mt-1">Portfolio-wide tracking across all GST registrations and filings.</p>
      </div>
      <div className="flex items-center gap-1 border-b border-emerald-900/30 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`gst-compliance-tab-${t.id}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === t.id ? 'border-emerald-400 text-emerald-300' : 'border-transparent text-slate-400 hover:text-white'}`}>
            <span className="inline-flex items-center gap-1.5"><t.icon className="w-4 h-4" /> {t.label}</span>
          </button>
        ))}
      </div>
      {tab === 'summary' && <PortfolioSummary />}
      {tab === 'companies' && <CompanyList onOpen={setOpenCompany} />}
      {tab === 'filings' && <FilingTracker onOpen={setOpenCompany} />}
      {tab === 'payments' && <PaymentTracker onOpen={setOpenCompany} />}
    </div>
  );
};

export default GstCompliance;
